import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import { 
  Send, User, ArrowLeft, MoreVertical, Image, Paperclip, 
  Ban, Flag, Loader2, Check, CheckCheck, X, MessageSquare,
  Phone, ExternalLink
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// WebSocket connection hook
const useWebSocket = (userId, onMessage) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  
  const connect = useCallback(() => {
    if (!userId) return;
    
    const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/chat/ws/${userId}`);
    
    ws.onopen = () => {
      console.log('WebSocket connected');
      // Start ping interval
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);
      ws.pingInterval = pingInterval;
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type !== 'pong') {
          onMessage(data);
        }
      } catch (e) {
        console.error('Failed to parse WebSocket message:', e);
      }
    };
    
    ws.onclose = () => {
      console.log('WebSocket disconnected');
      if (ws.pingInterval) clearInterval(ws.pingInterval);
      // Reconnect after 3 seconds
      reconnectTimeoutRef.current = setTimeout(connect, 3000);
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    wsRef.current = ws;
  }, [userId, onMessage]);
  
  useEffect(() => {
    connect();
    
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        if (wsRef.current.pingInterval) clearInterval(wsRef.current.pingInterval);
        wsRef.current.close();
      }
    };
  }, [connect]);
  
  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);
  
  return { sendMessage };
};

const ChatPage = () => {
  const { conversationId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { formatCurrency } = useCountry();
  
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  
  const token = localStorage.getItem('token');
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  
  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((data) => {
    if (data.type === 'new_message') {
      if (data.conversation_id === selectedConversation?.id) {
        setMessages(prev => [...prev, {
          ...data.message,
          is_own: data.message.sender_id === currentUser.id
        }]);
      }
      // Update conversation list
      setConversations(prev => prev.map(conv => {
        if (conv.id === data.conversation_id) {
          return {
            ...conv,
            last_message: {
              content: data.message.content,
              created_at: data.message.created_at,
              is_own: data.message.sender_id === currentUser.id
            },
            unread_count: data.conversation_id === selectedConversation?.id ? 0 : conv.unread_count + 1
          };
        }
        return conv;
      }));
    }
    
    if (data.type === 'typing') {
      setTypingUsers(prev => ({
        ...prev,
        [data.conversation_id]: data.is_typing ? data.user_name : null
      }));
    }
    
    if (data.type === 'messages_read' && data.conversation_id === selectedConversation?.id) {
      setMessages(prev => prev.map(msg => ({
        ...msg,
        read: msg.is_own ? true : msg.read
      })));
    }
  }, [selectedConversation, currentUser.id]);
  
  const { sendMessage: sendWsMessage } = useWebSocket(currentUser.id, handleWebSocketMessage);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Fetch conversations
  useEffect(() => {
    if (!token) {
      navigate('/login', { state: { from: '/messages' } });
      return;
    }
    fetchConversations();
  }, [token]);
  
  // Handle URL parameter for starting new conversation
  useEffect(() => {
    const listingId = searchParams.get('listing');
    const sellerId = searchParams.get('seller');
    
    if (listingId && sellerId) {
      startNewConversation(listingId, sellerId);
    }
  }, [searchParams]);
  
  // Load conversation from URL
  useEffect(() => {
    if (conversationId && conversations.length > 0) {
      const conv = conversations.find(c => c.id === conversationId);
      if (conv) {
        selectConversation(conv);
      }
    }
  }, [conversationId, conversations]);
  
  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/chat/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      toast({
        title: 'Error',
        description: 'Failed to load conversations',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const startNewConversation = async (listingId, sellerId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/chat/conversations`,
        { listing_id: listingId, seller_id: sellerId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      await fetchConversations();
      navigate(`/messages/${response.data.id}`);
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to start conversation',
        variant: 'destructive'
      });
    }
  };
  
  const selectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    navigate(`/messages/${conversation.id}`);
    
    try {
      const response = await axios.get(
        `${API_URL}/api/chat/conversations/${conversation.id}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(response.data);
      
      // Reset unread count
      setConversations(prev => prev.map(conv => 
        conv.id === conversation.id ? { ...conv, unread_count: 0 } : conv
      ));
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };
  
  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedConversation || sendingMessage) return;
    
    try {
      setSendingMessage(true);
      const response = await axios.post(
        `${API_URL}/api/chat/conversations/${selectedConversation.id}/messages`,
        { content: messageText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessages(prev => [...prev, response.data]);
      setMessageText('');
      
      // Update conversation list
      setConversations(prev => prev.map(conv => {
        if (conv.id === selectedConversation.id) {
          return {
            ...conv,
            last_message: {
              content: messageText,
              created_at: new Date().toISOString(),
              is_own: true
            }
          };
        }
        return conv;
      }));
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to send message',
        variant: 'destructive'
      });
    } finally {
      setSendingMessage(false);
    }
  };
  
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedConversation) return;
    
    try {
      setUploadingFile(true);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadResponse = await axios.post(
        `${API_URL}/api/chat/upload`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Send message with attachment
      const response = await axios.post(
        `${API_URL}/api/chat/conversations/${selectedConversation.id}/messages`,
        { 
          content: file.name,
          attachment_url: uploadResponse.data.url,
          attachment_type: uploadResponse.data.type
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessages(prev => [...prev, response.data]);
      
      toast({
        title: 'Success',
        description: 'File uploaded successfully'
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to upload file',
        variant: 'destructive'
      });
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const handleTyping = () => {
    if (!selectedConversation) return;
    
    sendWsMessage({
      type: 'typing',
      conversation_id: selectedConversation.id,
      is_typing: true
    });
    
    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set typing to false after 2 seconds of no input
    typingTimeoutRef.current = setTimeout(() => {
      sendWsMessage({
        type: 'typing',
        conversation_id: selectedConversation.id,
        is_typing: false
      });
    }, 2000);
  };
  
  const handleBlockUser = async () => {
    if (!selectedConversation) return;
    
    try {
      await axios.post(
        `${API_URL}/api/chat/block`,
        { blocked_user_id: selectedConversation.other_user.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: 'User Blocked',
        description: 'You will no longer receive messages from this user'
      });
      
      // Remove conversation from list
      setConversations(prev => prev.filter(c => c.id !== selectedConversation.id));
      setSelectedConversation(null);
      navigate('/messages');
    } catch (error) {
      console.error('Error blocking user:', error);
      toast({
        title: 'Error',
        description: 'Failed to block user',
        variant: 'destructive'
      });
    }
  };
  
  const handleReport = async () => {
    if (!selectedConversation || !reportReason) return;
    
    try {
      await axios.post(
        `${API_URL}/api/chat/report`,
        {
          reported_user_id: selectedConversation.other_user.id,
          conversation_id: selectedConversation.id,
          reason: reportReason,
          description: reportDescription
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: 'Report Submitted',
        description: 'Thank you for your report. We will review it shortly.'
      });
      
      setShowReportDialog(false);
      setReportReason('');
      setReportDescription('');
    } catch (error) {
      console.error('Error reporting user:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit report',
        variant: 'destructive'
      });
    }
  };
  
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };
  
  if (!token) {
    return null;
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-3xl font-bold">Messages</h1>
          <p className="text-blue-100">Chat with buyers and sellers</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <Card className="overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-3 h-[600px]">
            {/* Conversations List */}
            <div className="border-r flex flex-col">
              <CardHeader className="border-b py-4">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>Conversations</span>
                  {conversations.filter(c => c.unread_count > 0).length > 0 && (
                    <Badge variant="destructive">
                      {conversations.reduce((sum, c) => sum + c.unread_count, 0)}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              
              <ScrollArea className="flex-1">
                {loading ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                  </div>
                ) : conversations.length > 0 ? (
                  conversations.map((conv) => (
                    <div
                      key={conv.id}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors border-b ${
                        selectedConversation?.id === conv.id ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => selectConversation(conv)}
                    >
                      <div className="flex items-start space-x-3">
                        <div className="relative">
                          <Avatar>
                            <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-500 text-white">
                              {conv.other_user.name.charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          {conv.is_online && (
                            <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <p className="font-semibold text-sm truncate">{conv.other_user.name}</p>
                            <span className="text-xs text-gray-400">
                              {conv.last_message && formatTime(conv.last_message.created_at)}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 truncate mb-1">{conv.listing.title}</p>
                          <div className="flex items-center justify-between">
                            <p className="text-sm text-gray-600 truncate">
                              {conv.last_message?.is_own && 'You: '}
                              {conv.last_message?.has_attachment ? '📎 Attachment' : conv.last_message?.content || 'No messages yet'}
                            </p>
                            {conv.unread_count > 0 && (
                              <Badge className="ml-2 bg-blue-600">{conv.unread_count}</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No messages yet</p>
                    <p className="text-sm mt-2">Start a conversation by contacting a seller</p>
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Chat Area */}
            <div className="md:col-span-2 flex flex-col">
              {selectedConversation ? (
                <>
                  {/* Chat Header */}
                  <CardHeader className="border-b py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="md:hidden"
                          onClick={() => {
                            setSelectedConversation(null);
                            navigate('/messages');
                          }}
                        >
                          <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <Avatar>
                          <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-500 text-white">
                            {selectedConversation.other_user.name.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-semibold">{selectedConversation.other_user.name}</p>
                          <p className="text-xs text-gray-500">
                            {selectedConversation.is_online ? (
                              <span className="text-green-600">Online</span>
                            ) : 'Offline'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/listing/${selectedConversation.listing.id}`)}
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          View Ad
                        </Button>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-5 w-5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setShowReportDialog(true)}>
                              <Flag className="h-4 w-4 mr-2" />
                              Report User
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={handleBlockUser}
                              className="text-red-600"
                            >
                              <Ban className="h-4 w-4 mr-2" />
                              Block User
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                    
                    {/* Listing Preview */}
                    <div 
                      className="mt-3 p-2 bg-gray-50 rounded-lg flex items-center space-x-3 cursor-pointer hover:bg-gray-100"
                      onClick={() => navigate(`/listing/${selectedConversation.listing.id}`)}
                    >
                      {selectedConversation.listing.image && (
                        <img
                          src={`${API_URL}${selectedConversation.listing.image}`}
                          alt={selectedConversation.listing.title}
                          className="w-12 h-12 object-cover rounded"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{selectedConversation.listing.title}</p>
                        {selectedConversation.listing.price && (
                          <p className="text-blue-600 font-semibold text-sm">
                            {formatCurrency(selectedConversation.listing.price)}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardHeader>

                  {/* Messages */}
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-4">
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.is_own ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[70%] rounded-lg p-3 ${
                              msg.is_own
                                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                                : 'bg-gray-100 text-gray-900'
                            }`}
                          >
                            {msg.attachment_url && (
                              <div className="mb-2">
                                {msg.attachment_type === 'image' ? (
                                  <img
                                    src={`${API_URL}${msg.attachment_url}`}
                                    alt="Attachment"
                                    className="max-w-full rounded cursor-pointer"
                                    onClick={() => window.open(`${API_URL}${msg.attachment_url}`, '_blank')}
                                  />
                                ) : (
                                  <a
                                    href={`${API_URL}${msg.attachment_url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`flex items-center space-x-2 ${msg.is_own ? 'text-blue-100' : 'text-blue-600'}`}
                                  >
                                    <Paperclip className="h-4 w-4" />
                                    <span className="underline">{msg.content}</span>
                                  </a>
                                )}
                              </div>
                            )}
                            {(!msg.attachment_url || msg.attachment_type !== 'image') && (
                              <p className="text-sm">{msg.content}</p>
                            )}
                            <div className={`flex items-center justify-end space-x-1 mt-1 ${
                              msg.is_own ? 'text-blue-100' : 'text-gray-500'
                            }`}>
                              <span className="text-xs">
                                {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                              {msg.is_own && (
                                msg.read ? (
                                  <CheckCheck className="h-3 w-3" />
                                ) : (
                                  <Check className="h-3 w-3" />
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {/* Typing Indicator */}
                      {typingUsers[selectedConversation.id] && (
                        <div className="flex justify-start">
                          <div className="bg-gray-100 rounded-lg px-4 py-2">
                            <div className="flex space-x-1">
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>

                  {/* Message Input */}
                  <div className="p-4 border-t">
                    <div className="flex items-center space-x-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        accept="image/*,.pdf"
                        className="hidden"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingFile}
                      >
                        {uploadingFile ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          <Paperclip className="h-5 w-5" />
                        )}
                      </Button>
                      
                      <Input
                        placeholder="Type your message..."
                        value={messageText}
                        onChange={(e) => {
                          setMessageText(e.target.value);
                          handleTyping();
                        }}
                        onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                        className="flex-1"
                      />
                      
                      <Button
                        onClick={handleSendMessage}
                        disabled={!messageText.trim() || sendingMessage}
                        className="bg-gradient-to-r from-blue-600 to-purple-600"
                      >
                        {sendingMessage ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <MessageSquare className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg mb-2">Select a conversation</p>
                    <p className="text-sm">Choose a conversation from the left to start messaging</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
      
      {/* Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report User</DialogTitle>
            <DialogDescription>
              Please provide details about why you are reporting this user.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Reason</label>
              <select
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                className="w-full mt-1 p-2 border rounded-md"
              >
                <option value="">Select a reason</option>
                <option value="spam">Spam</option>
                <option value="harassment">Harassment</option>
                <option value="scam">Scam/Fraud</option>
                <option value="inappropriate">Inappropriate Content</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div>
              <label className="text-sm font-medium">Additional Details</label>
              <Textarea
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                placeholder="Please provide more details..."
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleReport}
              disabled={!reportReason}
              className="bg-red-600 hover:bg-red-700"
            >
              Submit Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChatPage;
